import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BRAND } from "@/brand.config";

const HERO = "/bh/hero.jpg";
const HIM = "/bh/him.jpg";
const HER = "/bh/her.jpg";
const HERITAGE = "/bh/heritage.jpg";
const CAMPAIGN = "/bh/campaign.jpg";

const FALLBACK_TIERS = [
  { name: "Silver", min_lifetime_spend: 0, earn_multiplier: 1.0, birthday_bonus: 200, tier_type: "entry" },
  { name: "Gold", min_lifetime_spend: 25000, earn_multiplier: 1.25, birthday_bonus: 500, tier_type: "standard" },
  { name: "Platinum", min_lifetime_spend: 75000, earn_multiplier: 1.5, birthday_bonus: 1000, tier_type: "premium" },
  { name: "Diamond", min_lifetime_spend: 150000, earn_multiplier: 2.0, birthday_bonus: 2000, tier_type: "elite" },
];

const fmtSpend = (n) => {
  if (!n || n <= 0) return "₹0";
  if (n >= 100000) return `₹${(n / 100000).toFixed(n % 100000 ? 1 : 0)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}k`;
  return `₹${n}`;
};
const fmtMult = (m) => `${Number(m || 1).toString()}x`;

const GRID_COLS = { 1: "lg:grid-cols-1", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6" };

export default function Home() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", city: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [cms, setCms] = useState(null);
  const [tiers, setTiers] = useState(FALLBACK_TIERS);

  useEffect(() => {
    document.title = BRAND.meta.title;
    api.get("/cms/content").then((r) => setCms(r.data)).catch(() => {});
    api.get("/public/tiers").then((r) => {
      if (r.data?.tiers?.length) setTiers(r.data.tiers);
    }).catch(() => {});
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", BRAND.meta.description);
  }, []);

  const c = cms?.home || {};

  const submit = async (e) => {
    e.preventDefault();
    if (!form.mobile || form.mobile.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/public/register-interest", form);
      setResult(r.data);
      if (r.data.already_registered) toast.success(`Welcome back! ${r.data.tier?.toUpperCase()} member · ${r.data.points} points.`);
      else toast.success(BRAND.welcomeToast);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-4 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-white/70 transition-colors";
  const inputSty = { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.18)", color: "#fff" };

  return (
    <div className="bg-[#0A0A0A] text-white">
      {/* HERO — full-bleed heritage */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden bg-[#141414]" data-testid="hero">
        <img
          ref={(el) => { if (el && el.complete) el.style.opacity = "1"; }}
          src={c.hero_image_url || HERO}
          alt="Beverly Hills Polo Club"
          fetchpriority="high"
          loading="eager"
          decoding="async"
          onError={(e) => { if (e.currentTarget.src !== window.location.origin + HERO) e.currentTarget.src = HERO; }}
          onLoad={(e) => { e.currentTarget.style.opacity = "1"; }}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out"
          style={{ opacity: 0 }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0.35) 100%)" }} />
        <div className="relative max-w-[1400px] mx-auto w-full px-6 lg:px-12 pb-20 lg:pb-28">
          <p className="text-[11px] uppercase tracking-[0.35em] text-white/70 mb-6 fade-up">{c.hero_eyebrow || "The Loyalty Club · Since 1982"}</p>
          <h1 className="editorial-headline text-5xl md:text-7xl lg:text-8xl max-w-4xl leading-[0.95] fade-up" style={{ animationDelay: "0.1s" }}>
            {c.hero_headline_1 || "Where heritage"}<br />
            <em className="font-light">{c.hero_headline_em || "rewards"}</em> {c.hero_headline_2 || "you."}
          </h1>
          <p className="mt-7 text-white/70 text-base md:text-lg max-w-xl leading-relaxed fade-up" style={{ animationDelay: "0.2s" }}>
            {c.hero_subtext || BRAND.homeCopy.heroSubtext}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-5 fade-up" style={{ animationDelay: "0.3s" }}>
            <a href="#join" className="text-[12px] uppercase tracking-[0.2em] px-7 py-3.5 bg-white text-black hover:bg-white/90 transition-colors inline-flex items-center gap-2" data-testid="hero-join-cta">
              {BRAND.ctaJoinFree} <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/loyalty-benefits" className="text-[12px] uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors inline-flex items-center gap-2">
              Discover benefits <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <a href="#join" className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 hover:text-white transition-colors" aria-label="Scroll"><ChevronDown className="w-6 h-6 animate-bounce" /></a>
      </section>

      {/* JOIN */}
      <section id="join" className="border-t border-white/10 py-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-white/50 mb-5">Membership</p>
            <h2 className="editorial-headline text-5xl lg:text-6xl leading-[0.95] mb-6">Become<br /><em className="font-light">a member.</em></h2>
            <p className="text-white/60 max-w-md leading-relaxed">Free to join. Earn on every purchase across BHPC boutiques, unlock tier privileges, and receive {BRAND.welcomePointsValue} welcome points instantly.</p>
            <div className="mt-10 flex items-center gap-10 text-xs text-white/40">
              <div><div className="text-3xl font-display text-white">{c.stats_members || "1.5L+"}</div><div className="uppercase tracking-[0.2em] mt-1">Members</div></div>
              <div className="h-10 w-px bg-white/15" />
              <div><div className="text-3xl font-display text-white">{c.stats_cities || "15+"}</div><div className="uppercase tracking-[0.2em] mt-1">Cities</div></div>
              <div className="h-10 w-px bg-white/15" />
              <div><div className="text-3xl font-display text-white">{c.stats_stores || "25+"}</div><div className="uppercase tracking-[0.2em] mt-1">Stores</div></div>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-3 max-w-md w-full lg:justify-self-end">
            <div className="grid grid-cols-2 gap-3">
              <input style={inputSty} className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hero-input-name" />
              <input style={inputSty} className={inputCls} placeholder="Mobile number" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="hero-input-mobile" />
            </div>
            <input style={inputSty} className={inputCls} placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="hero-input-email" />
            <input style={inputSty} className={inputCls} placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="hero-input-city" />
            <button type="submit" disabled={submitting} className="w-full px-5 py-3.5 bg-white text-black text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="hero-submit-btn">
              {submitting ? "Joining…" : BRAND.ctaJoinFree} <ArrowRight className="w-4 h-4" />
            </button>
            {result && (
              <div className="text-sm text-white/70 pt-1">
                {result.already_registered ? `Welcome back · ${result.tier?.toUpperCase()} · ${result.points} pts` : `You're in! +${result.welcome_bonus} welcome points credited.`}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* COLLECTION-STYLE BANDS (For members / Your rewards) */}
      <section className="grid md:grid-cols-2">
        {[
          { img: HIM, eyebrow: "Earn", lead: "For members", to: "/earn-points" },
          { img: HER, eyebrow: "Redeem", lead: "Your rewards", to: "/rewards" },
        ].map((b) => (
          <Link key={b.lead} to={b.to} className="group relative aspect-[4/3] overflow-hidden" data-testid={`band-${b.eyebrow.toLowerCase()}`}>
            <img src={b.img} alt={b.lead} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/25 group-hover:bg-black/35 transition-colors" />
            <div className="absolute bottom-8 left-8">
              <div className="text-[11px] uppercase tracking-[0.3em] text-white/70">{b.eyebrow}</div>
              <div className="editorial-headline text-3xl md:text-4xl mt-1">{b.lead}</div>
            </div>
          </Link>
        ))}
      </section>

      {/* TIERS — BHPC World index style */}
      <section className="py-24 px-6 lg:px-12 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto">
          <p className="text-[11px] uppercase tracking-[0.35em] text-white/50 mb-4">The Tiers</p>
          <h2 className="editorial-headline text-5xl lg:text-7xl max-w-3xl mb-14">Every tier,<br /><em className="font-light">a new privilege.</em></h2>
          <div className={`grid md:grid-cols-2 ${GRID_COLS[Math.min(tiers.length, 6)] || "lg:grid-cols-4"} gap-px bg-white/10 border border-white/10`}>
            {tiers.map((t, i) => (
              <div key={t.tier || t.name} className="bg-[#0A0A0A] p-8 fade-up" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`tier-card-${(t.tier || t.name).toLowerCase()}`}>
                <div className="flex items-baseline justify-between mb-8">
                  <span className="text-[11px] uppercase tracking-[0.25em] text-white/50">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[12px] uppercase tracking-[0.2em]">{t.name}</span>
                </div>
                <div className="editorial-headline text-5xl mb-1">{fmtMult(t.earn_multiplier)}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-6">Earn rate</div>
                <div className="space-y-2.5 text-sm text-white/70">
                  <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/40">Min. spend</span><span>{fmtSpend(t.min_lifetime_spend)}</span></div>
                  <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/40">Birthday</span><span>{t.birthday_bonus} pts</span></div>
                  <div className="flex justify-between"><span className="text-white/40">Early access</span><span>{(t.tier_type === "entry" || i === 0) ? "—" : "✓"}</span></div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/loyalty-benefits" className="text-[12px] uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors inline-flex items-center gap-2">Compare all benefits <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* HERITAGE STORY */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden border-t border-white/10">
        <img src={c.editorial_image_url || HERITAGE} alt="Beverly Hills Polo Club heritage" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-[1400px] mx-auto w-full px-6 lg:px-12 text-center">
          <p className="text-[11px] uppercase tracking-[0.35em] text-white/70 mb-5">Since 1982</p>
          <h2 className="editorial-headline text-5xl lg:text-7xl mb-6">Our story is unique.</h2>
          <p className="text-white/70 max-w-xl mx-auto leading-relaxed mb-8">A story that began in 1982, and has been building ever since — now rewarding the members who make it timeless.</p>
          <Link to="/about-program" className="text-[12px] uppercase tracking-[0.2em] text-white/80 hover:text-white transition-colors inline-flex items-center gap-2">Discover our heritage <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6 lg:px-12 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-white/50 mb-4">Simply</p>
            <h2 className="editorial-headline text-5xl lg:text-7xl mb-8">Three steps.<br /><em className="font-light">A lifetime of rewards.</em></h2>
            <Link to="/how-it-works" className="text-[12px] uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors inline-flex items-center gap-2">See full guide <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
          <div className="space-y-8">
            {[
              { step: "01", title: "Join free", body: `Sign up at any ${BRAND.name} boutique or online with your mobile number. Receive ${BRAND.welcomePointsValue} welcome points instantly.` },
              { step: "02", title: "Earn effortlessly", body: "Quote your mobile at checkout. Points accrue automatically on every ₹1 spent." },
              { step: "03", title: "Redeem your way", body: "1 point = ₹0.25. Use them on your next purchase, in any boutique, online or off." },
            ].map((s) => (
              <div key={s.step} className="flex gap-8 border-b border-white/10 pb-8 last:border-0">
                <div className="editorial-headline text-5xl text-white/40" style={{ fontWeight: 300 }}>{s.step}</div>
                <div>
                  <h3 className="editorial-headline text-2xl mb-1.5">{s.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed max-w-md">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden border-t border-white/10" style={{ minHeight: 520 }}>
        <img src={c.boutique_image_url || CAMPAIGN} alt="Beverly Hills Polo Club" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-3xl mx-auto px-6 py-28 text-center">
          <p className="text-[11px] uppercase tracking-[0.35em] text-white/60 mb-6">Join today</p>
          <h2 className="editorial-headline text-5xl lg:text-7xl mb-8">{c.final_cta_headline || "Membership is free."}<br /><em className="font-light">Privileges are forever.</em></h2>
          <a href="#join" className="text-[12px] uppercase tracking-[0.2em] px-8 py-4 bg-white text-black hover:bg-white/90 transition-colors inline-flex items-center gap-2" data-testid="cta-final-join">
            Become a member <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
